import PillButton from "@/app/shared/PillButton";
import { Alert, styled, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { signIn } from "next-auth/react";
import { useState } from "react";
import { useAuthSearchParams } from "./useAuthSearchParams";
import { z } from "zod";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ControlledTextField from "../sell/(ad)/ad/form/ControlledTextField";

const TypographyLink = styled(Typography)(({ theme }) => ({
  cursor: "pointer",
  color: "black",
  "&:hover": {
    textDecoration: "underline",
  },
  ...theme.typography.caption,
  display: "block",
  textAlign: "right",
  textDecoration: "underline",
}));

const PasswordLoginViewModelSchema = z.object({
  emailOrUsername: z.string().min(1, "Email or username is required"),
  password: z.string().min(1, "Password is required"),
});

type PasswordLoginViewModel = z.infer<typeof PasswordLoginViewModelSchema>;

export default function PasswordLoginForm({
  onForgotClicked,
  defaultEmailOrUsername,
}: {
  onForgotClicked: (emailOrUsername: string) => void;
  defaultEmailOrUsername: string;
}) {
  const { returnUrl } = useAuthSearchParams();

  const methods = useForm<PasswordLoginViewModel>({
    resolver: zodResolver(PasswordLoginViewModelSchema),
    defaultValues: {
      emailOrUsername: defaultEmailOrUsername,
      password: "",
    },
  });
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={methods.handleSubmit(async (data) => {
          setIsLoading(true);
          setError("");
          try {
            const result = await signIn("credentials", {
              ...data,
              redirect: false,
            });
            console.log("Sign in result:", result);

            if (result.error) {
              setError(
                result.code ||
                  "Sorry, an error occurred during sign in. Please try again or contact us if the problem persists."
              );
            } else {
              window.location.href = returnUrl || "/";
            }
          } catch (err) {
            console.error("CLIENT_KNOWN_ERROR: Sign in error:", err);
            setError(
              "An unexpected error occurred. Please try again later or contact support."
            );
          } finally {
            setIsLoading(false);
          }
        })}
        sx={{
          padding: 4,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          backgroundColor: "white",
          alignItems: "center",
          borderRadius: 5,
        }}
      >
        <ControlledTextField
          label="Email / Username"
          name="emailOrUsername"
          type="text"
          fullWidth
        />
        <ControlledTextField
          label="Password"
          name="password"
          type="password"
          fullWidth
          helpText={
            <TypographyLink
              onClick={() =>
                onForgotClicked(methods.getValues("emailOrUsername"))
              }
            >
              Forgot Password?
            </TypographyLink>
          }
        />
        {error && <Alert severity="error">{error}</Alert>}
        <PillButton
          primary
          type="submit"
          loading={isLoading}
          disabled={isLoading}
          fullWidth
        >
          Sign In
        </PillButton>
        <PillButton
          sx={{
            backgroundColor: "grey.200",
          }}
          onClick={() => onForgotClicked(methods.getValues("emailOrUsername"))}
          fullWidth
        >
          Login without a password
        </PillButton>
      </Box>
    </FormProvider>
  );
}
